import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { Sites } from '@enums/sites';
import { environment } from '@environments/environment';
import { ErrorLoggingService } from '@services/error-logging.service';
import { OrderService } from '@services/order.service';
import { QueryParamsService } from '@services/query-params.service';
import { StorageService } from '@services/storage.service';

/**
 * Guard that controls access to the order page based on domain-specific conditions and the current state of the
 * application.
 */
@Injectable({
  providedIn: 'root',
})
export class OrderGuard implements CanActivate {
  private document = inject<Document>(DOCUMENT);
  private router = inject(Router);
  private storageService = inject(StorageService);
  private errorLoggingService = inject(ErrorLoggingService);
  private orderService = inject(OrderService);
  private queryParamsService = inject(QueryParamsService);

  /**
   * Determines if the order page can be activated based on domain-specific rules and the current application state.
   */
  public canActivate(): boolean {
    if (!this.storageService.transactionId) {
      // Not in the middle of any order, clear any traces of a previous order
      this.orderService.resetOrderStorage();
    }

    if (this.shouldRedirectToTests()) {
      this.document.location.href = this.queryParamsService.buildRedirectWithPersistedParams(
        environment.siteUrls.tests,
        this.document.location.href,
      );

      return false;
    }

    if (this.shouldRedirectToOrderAddress()) {
      this.router.navigateByUrl('/order-address.php');

      return false;
    }

    return true;
  }

  /**
   * Checks if the user should be redirected to the tests page based on domain-specific conditions.
   */
  private shouldRedirectToTests(): boolean {
    if (this.shouldRedirectConsultationDomain()) {
      const message = this.hasReorderId()
        ? 'Consultation domain reorder without tests: Redirect to tests page'
        : 'Consultation domain without tests: Redirect to tests page';

      this.errorLoggingService.logErrorWithStorage(message);

      return true;
    }

    if (environment.site === Sites.Stdcheck && !this.hasTests() && !this.hasReorder()) {
      const message = this.hasReorderId()
        ? 'Reorder without tests: Redirect to tests page'
        : 'No tests in url/storage: Redirect to tests page';

      this.errorLoggingService.logErrorWithStorage(message);

      return true;
    }

    return false;
  }

  /**
   * Checks if the user should be redirected for consultation domains like TreatMyUti, Starfish, or Stallion
   * when no tests are selected and it's not a reorder.
   */
  private shouldRedirectConsultationDomain(): boolean {
    const isTreatMyUtiOrStarfishWithoutReorder =
      ((environment.site as Sites) === Sites.Treatmyuti || (environment.site as Sites) === Sites.Starfish) &&
      !this.hasReorder();

    return (
      (isTreatMyUtiOrStarfishWithoutReorder ||
        (environment.site as Sites) === Sites.Stallion ||
        (environment.site as Sites) === Sites.TestosteroneHq) &&
      !this.storageService.tests?.length
    );
  }

  /**
   * Checks if the user should be redirected to the order address page.
   */
  private shouldRedirectToOrderAddress(): boolean {
    return environment.order.navigateToOrderAddressRoute && this.storageService.transactionId && !this.hasReorder();
  }

  /**
   * Checks if tests are selected either through URL parameters or storage.
   */
  private hasTests(): boolean {
    const url = new URL(this.document.location.href);

    return (
      ['panel', 'tests', 'tests[]', 'panel[]'].some((param) => !!url.searchParams.get(param)) ||
      !!this.storageService.tests?.length
    );
  }

  /**
   * Checks if the URL contains a reorderId parameter.
   */
  private hasReorderId(): boolean {
    const url = new URL(this.document.location.href);
    return !!url.searchParams.get('reorderId');
  }

  /**
   * Checks if the user has an account token cookie.
   */
  private hasAccountToken(): boolean {
    return !!this.storageService.getCookie('account_token');
  }

  /**
   * Determines if the current order is a reorder based on the presence of a reorderId in the URL and an account token cookie.
   */
  private hasReorder(): boolean {
    return this.hasReorderId() && this.hasAccountToken();
  }
}
